
import React from 'react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Info } from 'lucide-react';
import { useBusinessSubscriptionStatus } from '@/hooks/useBusinessSubscriptionStatus';

interface BusinessDeletionWarningProps {
  businessId: string;
}

const BusinessDeletionWarning = ({ businessId }: BusinessDeletionWarningProps) => {
  const { data: subscriptionInfo, isLoading } = useBusinessSubscriptionStatus(businessId);

  if (isLoading || !subscriptionInfo?.hasActiveSubscription) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // If in grace period, show informational message (deletion allowed)
  if (subscriptionInfo.isInGracePeriod) {
    return (
      <Alert className="border-amber-200 bg-amber-50">
        <Info className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-700">
          <strong>Grace Period Active</strong> - Your subscription has been canceled but is still active until {subscriptionInfo.subscriptionEndDate && formatDate(subscriptionInfo.subscriptionEndDate)}. 
          <span className="block mt-1 text-sm">
            You can delete your business now, but you will forfeit any remaining subscription benefits and time. This action cannot be undone and no refunds will be provided.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  // For active subscriptions (not in grace period), block deletion
  return (
    <Alert className="border-red-200 bg-red-50">
      <AlertTriangle className="h-4 w-4 text-red-600" />
      <AlertDescription className="text-red-700">
        <strong>Business cannot be deleted</strong> - This business has an active subscription that must be canceled and expired before deletion is allowed.
        {subscriptionInfo.subscriptionEndDate && (
          <span className="block mt-1 text-sm">
            Current subscription expires on: {formatDate(subscriptionInfo.subscriptionEndDate)}
          </span>
        )}
      </AlertDescription>
    </Alert>
  );
};

export default BusinessDeletionWarning;
